// A real page-shaped waiting surface; annotation data can arrive before the PDF does.
import { House } from "lucide-preact";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { displaySize } from "../../format/geometry.ts";
import { standbyLabels } from "../../layout/layout.ts";
import { retryScript, scriptLoad } from "../loading.ts";
import { usingOffline } from "../offline.ts";
import { ix, show, view } from "../state.ts";

function useFeedbackDelay(startedAt: number) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timers = [400, 1500].map((delay) => setTimeout(() => setTick((n) => n + 1), Math.max(0, startedAt + delay - performance.now())));
    return () => timers.forEach(clearTimeout);
  }, [startedAt]);
  const elapsed = performance.now() - startedAt;
  return { text: elapsed >= 400, indicator: elapsed >= 1500 };
}

function bytes(n: number): string {
  if (n < 1024) return `${Math.floor(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

function PageAnnotations({ page }: { page: number }) {
  const index = ix.value;
  const hiddenDepartments = view.value.hiddenDepartments;
  const hiddenLayers = view.value.hiddenLayers;
  const rows = useMemo(() => {
    if (!index) return [];
    const departments = new Set(index.sortedDepartments.filter((d) => !hiddenDepartments.includes(d.id)).map((d) => d.id));
    const standbys = standbyLabels(index, departments);
    return [
      ...index.doc.moments.filter((m) => m.anchor.page === page).map((m) => ({
        id: m.id, anchor: m.anchor,
        parts: index.cuesOf(m.id).filter((c) => departments.has(c.department)).map((c) => ({ title: index.cueRef(c), detail: c.label })),
      })),
      ...index.doc.standbys.filter((s) => s.anchor.page === page).map((s) => ({
        id: s.id, anchor: s.anchor, parts: standbys.get(s.id) ? [{ title: standbys.get(s.id)!, detail: "" }] : [],
      })),
      ...index.doc.notes.filter((n) => n.anchor.page === page && !hiddenLayers.includes(n.layer)).map((n) => ({
        id: n.id, anchor: n.anchor, parts: [{ title: index.layers.get(n.layer)?.name ?? "Note", detail: n.text }],
      })),
    ].filter((row) => row.parts.length).sort(index.compareAnchored);
  }, [index, page, hiddenDepartments, hiddenLayers]);
  if (!index) return null;
  return (
    <aside class="loading-annotations" aria-label={`Annotations on page ${index.pageLabel(page)}, read-only`}>
      <h2>Page {index.pageLabel(page)}</h2>
      <p class="hint">Annotations · read-only until the script is ready</p>
      {rows.length ? <ul>{rows.map((row) => <li key={row.id}>{row.parts.map((part, n) => <div key={n}><strong>{part.title}</strong>{part.detail && <p>{part.detail}</p>}</div>)}</li>)}</ul>
        : <p class="hint">No visible annotations on this page.</p>}
    </aside>
  );
}

function LoadingSurface({ opening = false }: { opening?: boolean }) {
  const load = scriptLoad.value;
  const feedback = useFeedbackDelay(load.startedAt);
  const surface = useRef<HTMLElement>(null);
  const errorHeading = useRef<HTMLHeadingElement>(null);
  const focusInside = useRef(false);
  const ready = load.stage === "ready";
  const failed = load.stage === "error";
  const offline = usingOffline.value;
  const index = ix.value;
  const count = index?.doc.project.source.pageCount ?? 0;
  const page = Math.max(1, Math.min(view.value.page, count || view.value.page));
  const metadata = index?.pages.get(page);
  const [width, height] = metadata ? displaySize(metadata) : [612, 792];
  const title = opening ? "q2cue" : index?.doc.project.title || show.value.title || "Script";
  const message = failed ? "The script couldn’t be opened"
    : opening || load.stage === "opening" ? "Opening show…"
    : load.stage === "preparing" ? "Preparing your page…"
    : offline ? "Opening offline copy…" : "Loading script…";
  const loaded = typeof load.loaded === "number" && Number.isFinite(load.loaded) && load.loaded >= 0 ? load.loaded : undefined;
  const total = typeof load.total === "number" && Number.isFinite(load.total) && load.total > 0 ? load.total : undefined;
  const determinate = load.stage === "loading" && loaded !== undefined && total !== undefined && loaded <= total;
  const percent = determinate ? Math.floor(loaded / total * 100) : undefined;
  const progressText = loaded === undefined ? "" : total !== undefined && loaded <= total ? `${bytes(loaded)} of ${bytes(total)}` : `${bytes(loaded)} downloaded`;

  useLayoutEffect(() => {
    if (opening) return;
    document.body.classList.toggle("script-unavailable", !ready);
    return () => document.body.classList.remove("script-unavailable");
  }, [opening, ready]);

  useLayoutEffect(() => {
    if (failed) {
      const active = document.activeElement;
      if (active === document.body || surface.current?.contains(active) || active?.closest("#viewerContainer, #gutterRail")) errorHeading.current?.focus({ preventScroll: true });
    }
    if (ready && focusInside.current && document.activeElement === document.body) {
      document.querySelector<HTMLInputElement>(".pager input")?.focus({ preventScroll: true });
      focusInside.current = false;
    }
  }, [failed, ready]);

  if (ready) return <p class="loading-sr-only" role="status" aria-live="polite">Script ready.</p>;
  return (
    <section ref={surface} class={`script-loading${opening ? " opening-show" : ""}`} aria-label={opening ? "Opening show" : "Script loading"} tabIndex={-1}
      onFocusCapture={() => { focusInside.current = true; }}
      onBlurCapture={(event) => { if (event.relatedTarget && !surface.current?.contains(event.relatedTarget as Node)) focusInside.current = false; }}>
      {opening && <header class="toolbar opening-toolbar"><a class="button" href="/"><House />All shows</a><strong>q2cue</strong></header>}
      <div class="loading-page-area">
        <div class="loading-paper" style={{ "--paper-ratio": String(width / height) }}>
          <header class="loading-paper-head"><strong>{title}</strong>{!opening && <span>Page {metadata?.label || (count ? page : "—")}</span>}</header>
          <div class={`loading-feedback${feedback.text || failed ? " visible" : ""}`}>
            <h1 ref={errorHeading} tabIndex={-1}>{message}</h1>
            <p class="loading-sr-only" role="status" aria-live="polite" aria-atomic="true">{feedback.text || failed ? message : ""}</p>
            {failed ? <>
              <p>Your cues and any unsaved work are kept. Try opening just the PDF again, or return to All shows.</p>
              <div class="loading-actions">
                {retryScript.value && <button type="button" class="primary" onClick={() => {
                  surface.current?.focus({ preventScroll: true });
                  retryScript.value?.();
                }}>Try again</button>}
                <a class="button" href="/">All shows</a>
              </div>
              {load.error && <details class="loading-details"><summary>Details</summary><p>{load.error.replace(/https?:\/\/[^\s"'<>]+|\/api\/shows\/[^\s"'<>]+/g, "[script address]")}</p></details>}
            </> : <>
              <p>{opening ? "Getting the show and its cues." : load.stage === "preparing" ? "Getting the PDF ready to display. Your cues are separate from the script." : offline ? "Reading the script stored on this device." : "Your page will appear here when it’s ready."}</p>
              {feedback.indicator && <div class="loading-transfer">
                <progress aria-label={load.stage === "preparing" ? "Preparing PDF page" : opening ? "Opening show" : "Script download"} aria-valuetext={load.stage === "loading" && progressText ? `${percent === undefined ? "" : `${percent}%; `}${progressText}` : undefined} max={determinate ? total : undefined} value={determinate ? loaded : undefined} />
                {load.stage === "loading" && <div class="loading-byte-progress" aria-hidden="true"><span>{progressText || "Downloading…"}</span>{percent !== undefined && <strong>{percent}%</strong>}</div>}
              </div>}
              {!opening && offline && <p class="hint">Using offline copy</p>}
            </>}
          </div>
        </div>
      </div>
      {!opening && <PageAnnotations page={page} />}
    </section>
  );
}

/** Before show data exists, only navigation is offered — not a pretend editor toolbar. */
export function OpeningShow() {
  return <LoadingSurface opening />;
}

/** Remains mounted after readiness so a focused retry can hand focus back to the pager. */
export function ScriptLoading() {
  return <LoadingSurface />;
}
